"use client";

import { useId } from "react";
import { classNames } from "./class-names";

export type OptionToggleProps = Readonly<{
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}>;

/** Row layout switch (WCAG: role=switch, ≥24px hit target). */
export function OptionToggle({
  label,
  description,
  checked,
  onChange,
}: OptionToggleProps) {
  const labelId = useId();
  const descriptionId = useId();

  return (
    <div className="flex items-start justify-between gap-3 py-0.5">
      <div className="min-w-0 flex-1">
        <span className="text-sm font-medium text-zinc-700" id={labelId}>
          {label}
        </span>
        {description ? (
          <p
            id={descriptionId}
            className="mt-0.5 text-xs font-normal leading-snug text-zinc-500"
          >
            {description}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelId}
        aria-describedby={description ? descriptionId : undefined}
        onClick={() => onChange(!checked)}
        className={classNames(
          "relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
          checked ? "bg-brand" : "bg-zinc-300",
        )}
      >
        <span
          aria-hidden="true"
          className={classNames(
            "inline-block size-5 rounded-full bg-white shadow-sm transition",
            checked ? "translate-x-[1.375rem]" : "translate-x-0.5",
          )}
        />
      </button>
    </div>
  );
}
